<template>
  <transition name="lead-modal">
    <div v-if="open" class="lead-modal" role="presentation" @mousedown.self="close">
      <section
        ref="dialog"
        class="lead-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-modal-title"
        @mousedown.stop
      >
        <button type="button" class="lead-modal__close" aria-label="Закрыть окно" @click="close">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
        </button>

        <div class="lead-modal__intro">
          <span>Бесплатная помощь с выбором</span>
          <h2 id="lead-modal-title">Подберём диван под вашу комнату</h2>
          <p>Расскажите, что вам важно. Менеджер предложит подходящие модели, ткани и комплектации и поможет проверить размеры.</p>
          <ul>
            <li>учтём размер комнаты и дверных проёмов;</li>
            <li>подберём ткань под детей и домашних животных;</li>
            <li>рассчитаем стоимость и доставку.</li>
          </ul>
        </div>

        <form class="lead-modal__form" novalidate @focusin="trackFormStart" @submit.prevent="submit">
          <label>
            <span>Ваше имя</span>
            <input
              ref="firstInput"
              v-model.trim="form.name"
              type="text"
              autocomplete="name"
              maxlength="80"
              placeholder="Как к вам обращаться"
              required
            >
          </label>

          <div class="lead-modal__row">
            <label>
              <span>Телефон</span>
              <input
                v-model.trim="form.phone"
                v-phone-mask
                type="tel"
                autocomplete="tel"
                inputmode="tel"
                maxlength="18"
                placeholder="+7 (999) 000-00-00"
                required
              >
            </label>
            <label>
              <span>Электронная почта</span>
              <input
                v-model.trim="form.email"
                type="email"
                autocomplete="email"
                maxlength="120"
                placeholder="name@mail.ru"
                required
              >
            </label>
          </div>

          <label>
            <span>Что нужно учесть</span>
            <textarea
              v-model.trim="form.comment"
              rows="4"
              maxlength="700"
              placeholder="Например: угловой диван для семьи, ширина стены 280 см, нужен ящик и спальное место"
            ></textarea>
          </label>

          <label class="lead-modal__consent">
            <input v-model="form.consent" type="checkbox" required>
            <span>Согласен на обработку персональных данных по <button type="button" @click="goPrivacy">политике конфиденциальности</button>.</span>
          </label>

          <p v-if="error" class="lead-modal__message lead-modal__message--error">{{ error }}</p>
          <p v-if="success" class="lead-modal__message lead-modal__message--success">Заявка отправлена. Мы свяжемся с вами по указанным контактам.</p>

          <button class="lead-modal__submit" type="submit" :disabled="loading || success">
            <span>{{ loading ? 'Отправляем…' : success ? 'Заявка отправлена' : 'Получить подборку диванов' }}</span>
            <svg v-if="!loading" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 5l5 5-5 5"/></svg>
          </button>

          <small class="lead-modal__note">Это не оформляет заказ и ни к чему не обязывает.</small>
        </form>
      </section>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { trackGoal, trackError } from '@/analytics/metrica'
import { LEGAL_DOCUMENT_VERSIONS, legalSubjectId } from '@/legal/consent'

const API_URL = 'https://sitebypro-server.ru/druzbamebel'

export default {
  name: 'HeaderLeadModal',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: '',
        phone: '',
        email: '',
        comment: '',
        consent: false
      },
      loading: false,
      success: false,
      error: '',
      previousOverflow: ''
    }
  },
  watch: {
    open(value) {
      if (value) this.onOpen()
      else this.onClose()
    },
    'form.phone'(value) {
      if (!this.phoneTracked && String(value || '').replace(/\D/g, '').length >= 11) {
        this.phoneTracked = true
        trackGoal('hlead_phone_complete')
      }
    },
    'form.email'(value) {
      if (!this.emailTracked && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ''))) {
        this.emailTracked = true
        trackGoal('hlead_email_complete')
      }
    },
    'form.consent'(value) { if (value) trackGoal('hlead_consent') }
  },
  beforeDestroy() {
    this.onClose()
  },
  methods: {
    onOpen() {
      trackGoal('hlead_open')
      this.formStarted = false
      this.phoneTracked = false
      this.emailTracked = false
      this.error = ''
      this.success = false
      this.previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.handleKeydown)
      this.$nextTick(() => this.$refs.firstInput && this.$refs.firstInput.focus())
    },
    onClose() {
      document.body.style.overflow = this.previousOverflow || ''
      document.removeEventListener('keydown', this.handleKeydown)
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    close() {
      if (!this.loading) this.$emit('close')
    },
    trackFormStart() {
      if (this.formStarted) return
      this.formStarted = true
      trackGoal('hlead_form_start')
    },
    goPrivacy() {
      trackGoal('hlead_privacy')
      this.$emit('close')
      if (this.$route.path !== '/privacy') this.$router.push('/privacy')
    },
    validate() {
      if (this.form.name.length < 2) return 'Укажите имя.'
      if (this.form.phone.replace(/\D/g, '').length < 11) return 'Проверьте номер телефона.'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) return 'Проверьте электронную почту.'
      if (!this.form.consent) return 'Подтвердите согласие на обработку данных.'
      return ''
    },
    async submit() {
      if (this.loading || this.success) return
      this.error = this.validate()
      if (this.error) { trackGoal('hlead_error', { stage: 'validation' }); return }
      trackGoal('hlead_submit')

      this.loading = true

      const source = `${window.location.origin}${this.$route.fullPath}`
      const details = [
        `Имя: ${this.form.name}`,
        `Страница обращения: ${source}`,
        this.form.comment ? `Комментарий: ${this.form.comment}` : 'Комментарий: не указан'
      ].join('\n')

      try {
        const response = await axios.post(
          `${API_URL}/user/work-reguest`,
          {
            type: {
              name: 'sofa-selection',
              title: 'Подбор дивана с сайта'
            },
            tail: {
              email: this.form.email,
              phone: this.form.phone,
              text: details
            },
            subject_id: legalSubjectId(),
            legal: {
              personal_data: {
                accepted: this.form.consent === true,
                document_version: LEGAL_DOCUMENT_VERSIONS.personalData
              }
            }
          },
          { timeout: 20000 }
        )

        if (String(response?.data?.status) !== '200') {
          throw new Error(response?.data?.data?.text || 'Не удалось отправить заявку.')
        }

        this.success = true
        this.error = ''
        trackGoal('hlead_success')
        this.form = {
          name: '',
          phone: '',
          email: '',
          comment: '',
          consent: false
        }
        window.dispatchEvent(new CustomEvent('druzhba-lead-sent', {
          detail: { placement: 'header', path: this.$route.path }
        }))
      } catch (error) {
        this.error = error?.response?.data?.data?.text || error?.message || 'Не удалось отправить заявку. Позвоните нам по номеру +7 964 888-99-05.'
        trackGoal('hlead_error', { stage: 'request' })
        trackError('header-lead', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.lead-modal{position:fixed;z-index:1000;inset:0;display:grid;place-items:center;padding:24px;background:rgba(28,23,19,.62);backdrop-filter:blur(5px)}.lead-modal__dialog{position:relative;display:grid;width:min(980px,100%);max-height:calc(100dvh - 48px);grid-template-columns:.88fr 1.12fr;overflow:auto;border-radius:24px;background:#fff;box-shadow:0 28px 90px rgba(30,23,18,.28);text-align:left}.lead-modal__close{position:absolute;z-index:2;top:18px;right:18px;display:grid;width:40px;height:40px;place-items:center;padding:0;border:1px solid #eadfd5;border-radius:50%;background:#fff;color:#4f443c;cursor:pointer}.lead-modal__close:hover{border-color:#b07c50;color:#8f603b;transform:rotate(4deg)}.lead-modal__close svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round}.lead-modal__intro{display:flex;min-height:610px;flex-direction:column;justify-content:flex-end;padding:54px;background:linear-gradient(145deg,#56716b 0%,#263c3f 55%,#15282d 100%);color:#fff}.lead-modal__intro>span{color:#fff;font-size:10px;font-weight:800;letter-spacing:.75px;text-transform:uppercase}.lead-modal__intro h2{max-width:420px;margin:15px 0 0;color:#fff;font-size:47px;font-weight:500;line-height:1.02;letter-spacing:-2px;text-shadow:0 2px 20px rgba(0,0,0,.18)}.lead-modal__intro p{max-width:440px;margin:23px 0 0;color:rgba(255,255,255,.88);font-size:13px;line-height:1.7}.lead-modal__intro ul{display:grid;gap:12px;margin:30px 0 0;padding:0;list-style:none}.lead-modal__intro li{display:flex;gap:11px;color:rgba(255,255,255,.92);font-size:11px;line-height:1.5}.lead-modal__intro li::before{width:7px;height:7px;flex:0 0 auto;margin-top:5px;border-radius:50%;background:#fff;content:''}.lead-modal__form{display:flex;flex-direction:column;justify-content:center;padding:64px 54px 48px}.lead-modal__form>label,.lead-modal__row label{display:grid;gap:8px}.lead-modal__form>label+label,.lead-modal__row,.lead-modal__row+label{margin-top:18px}.lead-modal__form label>span,.lead-modal__row label>span{color:#675b52;font-size:10px;font-weight:700}.lead-modal__form input,.lead-modal__form textarea{width:100%;border:1px solid #dfd3c9;border-radius:11px;outline:0;background:#fff;color:#2d2621;font:inherit;font-size:13px}.lead-modal__form input{height:50px;padding:0 15px}.lead-modal__form textarea{min-height:105px;padding:14px 15px;resize:vertical;line-height:1.55}.lead-modal__form input:-webkit-autofill,.lead-modal__form input:-webkit-autofill:hover,.lead-modal__form input:-webkit-autofill:focus{-webkit-text-fill-color:#2d2621;-webkit-box-shadow:0 0 0 1000px #fff inset;box-shadow:0 0 0 1000px #fff inset;transition:background-color 9999s ease-out}.lead-modal__form input:focus,.lead-modal__form textarea:focus{border-color:#b07c50;box-shadow:0 0 0 3px rgba(176,124,80,.11)}.lead-modal__row{display:grid;grid-template-columns:1fr 1fr;gap:12px}.lead-modal__consent{display:flex!important;grid-template-columns:none!important;align-items:flex-start;gap:10px!important;margin-top:18px!important;color:#776b63;font-size:10px;line-height:1.5}.lead-modal__consent input{width:16px;height:16px;flex:0 0 auto;margin:0;accent-color:#b07c50}.lead-modal__consent button{padding:0;border:0;background:transparent;color:#8f603b;font:inherit;text-decoration:underline;cursor:pointer}.lead-modal__message{margin:16px 0 0;padding:11px 13px;border-radius:9px;font-size:10px;line-height:1.5}.lead-modal__message--error{border:1px solid #ead4cd;background:#fff7f4;color:#9b3a30}.lead-modal__message--success{border:1px solid #d4e2cf;background:#f6fbf4;color:#3f603a}.lead-modal__submit{display:flex;width:100%;height:52px;align-items:center;justify-content:center;gap:11px;margin-top:19px;border:0;border-radius:11px;background:#b07c50;color:#fff;font-size:11px;font-weight:800;cursor:pointer}.lead-modal__submit:hover{background:#8f603b;transform:translateY(-1px)}.lead-modal__submit:disabled{opacity:.66;cursor:default;transform:none}.lead-modal__submit svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}.lead-modal__note{margin-top:11px;color:#92867d;font-size:9px;text-align:center}.lead-modal-enter-active,.lead-modal-leave-active{transition:opacity .2s ease}.lead-modal-enter-active .lead-modal__dialog,.lead-modal-leave-active .lead-modal__dialog{transition:transform .24s ease,opacity .2s ease}.lead-modal-enter,.lead-modal-leave-to{opacity:0}.lead-modal-enter .lead-modal__dialog,.lead-modal-leave-to .lead-modal__dialog{opacity:0;transform:translateY(14px) scale(.985)}@media(max-width:760px){.lead-modal{align-items:end;padding:0}.lead-modal__dialog{width:100%;max-height:94dvh;grid-template-columns:1fr;border-radius:20px 20px 0 0}.lead-modal__intro{min-height:0;padding:32px 22px 27px}.lead-modal__intro h2{max-width:520px;font-size:34px;letter-spacing:-1.2px}.lead-modal__intro p{margin-top:16px;font-size:11px}.lead-modal__intro ul{display:none}.lead-modal__form{padding:28px 22px 25px}.lead-modal__row{grid-template-columns:1fr}.lead-modal__close{top:12px;right:12px;width:36px;height:36px}}@media(max-width:420px){.lead-modal__intro h2{max-width:300px;font-size:30px}.lead-modal__form input{height:47px}.lead-modal__submit{height:49px}}
</style>
